import {
  useAccountQuery,
  useBalanceAnchorsQuery,
  useDeleteBalanceAnchorMutation,
  useRecordBalanceAnchorMutation,
  useSetAccountClosingAnchorMutation,
} from '@transactions/graphql';
import {today} from 'common/date';
import * as React from 'react';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import InputGroup from 'src/InputGroup';
import type {DateTime} from 'luxon';

/**
 * Mark an account closed (or reopen it) by designating a $0 closing balance
 * anchor — the mirror of the opening balance. Works for depository and
 * investment accounts alike. Self-contained (fetches its own state and
 * commits immediately) because closing is a two-step create-then-designate
 * that rolls back the created anchor if the designation is refused; the
 * server validates the real preconditions (zero balance, latest point, no
 * active Plaid connection).
 */
export const AccountClosingSection = ({accountId}: {accountId: string}) => {
  const {data: accountData, refetch: refetchAccount} = useAccountQuery({
    variables: {id: accountId},
    fetchPolicy: 'cache-and-network',
  });
  const account = accountData?.account;
  const closingAnchorId = account?.closingAnchorId ?? null;

  const {data: anchorsData, refetch: refetchAnchors} = useBalanceAnchorsQuery({
    variables: {accountId},
    fetchPolicy: 'cache-and-network',
  });
  const anchors = anchorsData?.balanceAnchors ?? [];
  const closingAnchor = anchors.find((a) => a._id === closingAnchorId) ?? null;

  // Gate all controls until both queries have resolved: acting before the
  // network lands could miss an existing designation (offering "close" on an
  // already-closed account) or clobber an unloaded anchor on the chosen date.
  const ready = account != null && anchorsData?.balanceAnchors != null;

  const [date, setDate] = React.useState<DateTime>(today());
  const [error, setError] = React.useState<string | null>(null);

  const [recordAnchor, {loading: recording}] = useRecordBalanceAnchorMutation();
  const [setClosingAnchor, {loading: setting}] =
    useSetAccountClosingAnchorMutation();
  const [deleteAnchor] = useDeleteBalanceAnchorMutation();
  const busy = recording || setting;

  const handleClose = async () => {
    setError(null);
    if (!ready) return;
    const iso = date.toISODate();
    if (!iso) {
      setError('Pick a valid closing date');
      return;
    }
    // recordBalanceAnchor upserts by (account, date): if an anchor already
    // exists on this date, its balance/source is overwritten. Capture the
    // prior values so a refused designation can be fully reverted.
    const prior = anchors.find((a) => a.date === iso) ?? null;
    try {
      const recorded = await recordAnchor({
        variables: {accountId, date: iso, balance: 0, source: 'manual'},
      });
      const anchorId = recorded.data?.recordBalanceAnchor._id;
      if (!anchorId) {
        throw new Error('Failed to record closing balance');
      }
      try {
        await setClosingAnchor({variables: {accountId, anchorId}});
      } catch (designationErr) {
        // Revert the write so a rejected designation changes nothing: delete
        // a freshly created anchor, or restore a pre-existing one's values.
        if (prior) {
          await recordAnchor({
            variables: {
              accountId,
              date: iso,
              balance: prior.balance,
              source: prior.source,
            },
          }).catch(() => undefined);
        } else {
          await deleteAnchor({variables: {anchorId}}).catch(() => undefined);
        }
        throw designationErr;
      }
      await Promise.all([refetchAccount(), refetchAnchors()]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to close the account',
      );
    }
  };

  const handleReopen = async () => {
    setError(null);
    try {
      // Clears the designation only — the $0 anchor stays as history.
      await setClosingAnchor({variables: {accountId, anchorId: null}});
      await Promise.all([refetchAccount(), refetchAnchors()]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to reopen the account',
      );
    }
  };

  return (
    <div className="border-border flex flex-col gap-3 rounded-md border p-3">
      <div className="flex flex-col">
        <span className="font-medium">Closed</span>
        <span className="text-muted-foreground text-sm">
          Mark the account closed: a $0 balance on its final date, after which
          nothing can be added. The balance renders as zero from then on.
          Reconciliation stays available for the history inside.
        </span>
      </div>
      {!ready ? (
        <span className="text-muted-foreground text-sm">Loading…</span>
      ) : closingAnchorId != null ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm">
            Closed{closingAnchor ? (
              <>
                {' '}on <span className="font-medium">{closingAnchor.date}</span>
              </>
            ) : null}
          </span>
          <Button
            variant="outline"
            disabled={setting}
            onClick={() => void handleReopen()}
          >
            Reopen
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <InputGroup label="Closing date">
            <DatePicker selectedDate={date} onChange={setDate} />
          </InputGroup>
          <div>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => void handleClose()}
            >
              Mark account closed
            </Button>
          </div>
        </div>
      )}
      {error != null && <span className="text-destructive text-sm">{error}</span>}
    </div>
  );
};
